import {
  CalendarIcon,
  ClockIcon,
  ExclamationIcon,
  LightningBoltIcon,
  RefreshIcon,
  UserAddIcon,
} from "@heroicons/react/outline";
import {
  BellPlus,
  Flag01,
  NavigationPointer01,
  Play,
} from "@untitled-ui/icons-react";
import { ComponentType, ReactNode, useState } from "react";
import { TimelineEntryKind } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { classNames } from "@helpers/css";

import {
  ProgressionEvent,
  dateColumnLabels,
  eventsOf,
  progressionOf,
  sentTo,
} from "./compliance-program-progression-stages";
import {
  TimelineRow,
  audience,
  channelLabels,
  sendingKinds,
  skipReasonLabels,
} from "./compliance-program-timeline-vocabulary";

/**
 * A review's plan as a rail: one row per event, from kickoff to the last due
 * date, with the day each one happens in a column beside it.
 *
 * Two kinds of event sit on it and must never look alike. Most happen on a day,
 * and say which. Some go out for each person as somebody acts, such as a manager
 * review opening as each self review comes in: those have no day of their own,
 * so their column says "Auto", their node is violet, and the line under them
 * says what sets them off.
 *
 * The one drawing of the plan. The review page's Timeline tab, the setup form's
 * Workflow panel and its Notifications tab all render it, so a draft and the page
 * that shows it once saved cannot look different.
 */

/** One reason when every skipped person shares it, which is the usual case. */
const sharedSkipReason = (row: TimelineRow) => {
  const reasons = new Set(row.skipped.map((skip) => skip.reason));
  return reasons.size === 1
    ? skipReasonLabels[row.skipped[0].reason]
    : "several reasons";
};

/**
 * The count of people one send leaves out, opening onto their names and reasons.
 * Only a sending row has anybody to leave out.
 */
const SkippedPeople = ({ row }: { row: TimelineRow }) => {
  const [expanded, setExpanded] = useState(false);
  if (!sendingKinds.includes(row.kind) || row.skippedCount === 0) {
    return null;
  }
  return (
    <>
      <button
        type="button"
        className="flex shrink-0 items-center gap-1 rounded-sm bg-amber-50 px-1.5 text-xs text-amber-800 hover:bg-amber-100"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <ExclamationIcon className="h-3.5 w-3.5 shrink-0 text-amber-600" />
        {row.skippedCount} skipped · {sharedSkipReason(row)}
      </button>
      {expanded && (
        <ul className="basis-full pl-6 text-xs text-gray-500">
          {row.skipped.map((skip, index) => (
            <li key={`${skip.user?.id ?? index}-${skip.reason}`}>
              {skip.user?.name ?? "Somebody who has left"} —{" "}
              {skipReasonLabels[skip.reason] ?? skip.reason}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

type Icon = ComponentType<{ className?: string }>;

/** One line under an event's heading: an icon saying what kind, then the thing. */
const Line = ({
  icon: LineIcon,
  className,
  iconClassName = "text-gray-400",
  children,
}: {
  icon: Icon;
  className?: string;
  iconClassName?: string;
  children: ReactNode;
}) => (
  <div
    className={classNames(
      "flex items-start gap-x-2 text-sm text-gray-500",
      className,
    )}
  >
    {/* Level with the first line of text: a 16px icon in a 20px line. Only the
        text wraps, under itself, so a narrow card never drops a line below its
        icon. */}
    <LineIcon
      className={classNames("mt-0.5 h-4 w-4 shrink-0", iconClassName)}
    />
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
      {children}
    </div>
  </div>
);

/**
 * Who a message is sent to, and who it leaves out, on one line under the event
 * it belongs to. The heading already names the event, so the line says only
 * "Sent to all managers in the review": the message's own name would repeat it.
 *
 * An automatic message carries a bell, not an envelope, because it goes out by
 * Slack or Teams as often as by email; a reminder the admin scheduled carries a
 * clock and the channels they picked.
 */
const Message = ({
  row,
  withNote,
}: {
  row: TimelineRow;
  withNote: boolean;
}) => {
  const label = useLabel();
  const MessageIcon =
    row.kind === TimelineEntryKind.Reminder ? ClockIcon : BellPlus;
  const channels = row.channels
    .map((channel) => channelLabels[channel] ?? channel)
    .join(", ");
  return (
    // Not dimmed here: the stop it sits in dims the whole row, and opacity
    // multiplies, so dimming again drew a past line at 0.36.
    <div className="flex flex-col">
      {/* The same quiet gray as the trigger line above it, so the card reads
          as one list of what happens, not a list of warnings. */}
      <Line icon={MessageIcon}>
        <span>{sentTo(audience(row, label) ?? "", row.recipientsKnown)}</span>
        {channels && (
          <span className="text-xs font-normal text-gray-400">
            · {channels}
          </span>
        )}
        {/* On the same line as the rest, wrapping only when it has to. */}
        {withNote && row.note && (
          <span className="font-normal text-gray-400">· {row.note}</span>
        )}
        <SkippedPeople row={row} />
      </Line>
    </div>
  );
};

/**
 * One stop on the rail: the node, the line through it, and the stop's own row of
 * the card, ruled off from the one above.
 *
 * The rule runs the full width of the card, through the rail, so each stop reads
 * as a row of a table. Above the node the rail stops at the first stop, and below
 * it at the last.
 */
const Stop = ({
  node,
  first,
  last,
  dimmed,
  children,
}: {
  node: ReactNode;
  first: boolean;
  last: boolean;
  dimmed: boolean;
  children: ReactNode;
}) => (
  <div
    className={classNames(
      "flex gap-3 pl-4",
      !first && "border-t border-gray-200",
      dimmed && "opacity-60",
    )}
  >
    <div className="flex w-6 shrink-0 flex-col items-center">
      {/* Puts the node's centre on the centre of the row's first line: 16px of
          padding plus half a 20px line is 26px down, and a 24px node centred
          there starts at 14px. */}
      <div className={classNames("h-3.5 w-0.5", !first && "bg-gray-200")} />
      {node}
      {!last && <div className="w-0.5 flex-1 bg-gray-200" />}
    </div>
    {/* Set in from the rail, so the text does not crowd the node. */}
    <div className="min-w-0 flex-1 py-4 pl-3 pr-4">{children}</div>
  </div>
);

/**
 * The rail's mark for one stop: a filled circle with an icon saying what it is.
 *
 * Kickoff is the one solid blue node. A day is filled grey. Something people set
 * off is filled violet: the colour is what says it has no day of its own. Named
 * for a screen reader when the name is all that tells it apart.
 */
const Node = ({
  icon: NodeIcon,
  tone,
  name = null,
}: {
  icon: Icon;
  tone: "start" | "date" | "trigger";
  name?: string | null;
}) => (
  <div
    role="img"
    aria-label={name ?? undefined}
    aria-hidden={name ? undefined : true}
    className={classNames(
      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
      tone === "start" && "bg-blue-600 text-white",
      tone === "date" && "bg-gray-200 text-gray-600",
      tone === "trigger" && "bg-violet-200 text-violet-800",
    )}
  >
    <NodeIcon className="h-3.5 w-3.5" />
  </div>
);

/** What one of an ongoing or recurring review's own rows is about. */
const otherIcon = (row: TimelineRow): Icon =>
  row.kind === TimelineEntryKind.Recurrence
    ? RefreshIcon
    : row.kind === TimelineEntryKind.OngoingEntry
      ? UserAddIcon
      : CalendarIcon;

/**
 * The node's icon: what kind of event it is. Something people set off carries the
 * lightning, whatever it is; the heading already says what.
 */
const eventIcon = (event: ProgressionEvent): Icon => {
  if (event.trigger) {
    return LightningBoltIcon;
  }
  switch (event.kind) {
    case "start":
      return Play;
    case "opens":
      return NavigationPointer01;
    case "due":
      return Flag01;
    case "message":
      return event.messages[0]?.kind === TimelineEntryKind.Reminder
        ? ClockIcon
        : BellPlus;
    default:
      return event.row ? otherIcon(event.row) : CalendarIcon;
  }
};

/**
 * One event: its day in a column of its own, then what happens as the heading,
 * then, for a message, who it is sent to.
 */
const EventStop = ({
  event,
  first,
  last,
  dateLabel,
  wideDate,
}: {
  event: ProgressionEvent;
  first: boolean;
  last: boolean;
  /** The column holds a year too, so it is wider and never wraps. */
  wideDate: boolean;
  /** The column's text for this row, from `dateColumnLabels`. */
  dateLabel: string | null;
}) => (
  <Stop
    first={first}
    last={last}
    dimmed={event.isPast}
    node={
      <Node
        icon={eventIcon(event)}
        tone={
          event.kind === "start" ? "start" : event.trigger ? "trigger" : "date"
        }
        name={
          event.kind === "start"
            ? "Kickoff"
            : event.kind === "due"
              ? "Due"
              : null
        }
      />
    }
  >
    <div className="flex gap-4">
      <div
        className={classNames(
          "shrink-0 whitespace-nowrap text-sm tabular-nums text-gray-500",
          wideDate ? "w-24" : "w-14",
        )}
      >
        {/* A row people set off has no day; it happens on its own as they act,
            and the column says so rather than sitting blank, which would read
            as "the same day as above". */}
        {event.date ? dateLabel : <span className="text-violet-400">Auto</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{event.title}</div>
        {event.trigger && (
          <div className="mt-1">
            <Line icon={LightningBoltIcon}>{event.trigger}</Line>
          </div>
        )}
        {event.messages.length > 0 && (
          <div className="mt-1 flex flex-col gap-1.5">
            {event.messages.map((row, index) => (
              <Message
                key={`${row.label}-${index}`}
                row={row}
                // The trigger line already says what a set-off message's note
                // would.
                withNote={!event.trigger}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  </Stop>
);

/**
 * `empty` is what the card says when there is nothing to draw, which is never the
 * same sentence twice: a page that failed to load, a form with no dates yet and a
 * review that really sends nothing are three different answers and only the
 * caller knows which one it has.
 */
export const ComplianceProgramProgression = ({
  rows,
  empty,
}: {
  rows: readonly TimelineRow[];
  empty: ReactNode;
}) => {
  const label = useLabel();
  // Not memoised: every caller builds its rows array in the render that uses it,
  // so a memo on that identity would never hit.
  const events = eventsOf(progressionOf(rows, label), label);
  if (events.length === 0) {
    return (
      <div className="rounded-sm border border-gray-200 bg-white p-4 text-center text-sm text-gray-500">
        {empty}
      </div>
    );
  }
  const { labels, withYear } = dateColumnLabels(events);
  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      {events.map((event, index) => (
        <EventStop
          key={event.key}
          event={event}
          first={index === 0}
          last={index === events.length - 1}
          dateLabel={labels[index]}
          wideDate={withYear}
        />
      ))}
    </div>
  );
};
